import * as React from 'react';
import classNames from 'classnames';
import {withI18n, WithI18nProps} from '@shopify/react-i18n';
import {FocusTrap} from 'components/FocusTrap';
import {Flex, Icon, Text, Link, CheckBox, preventEventBubbling, NOOP} from 'components/UIKit';
import {LoginMethod} from './types';
import {LoginButton} from './components';
import style from './login.scss';
import * as locales from './locales';

interface LoginProps {
  show: boolean;
  methods: LoginMethod[];
  onClose?: () => void;
}

interface LoginState {
  subscribe: boolean;
}

class LoginComponent extends React.PureComponent<LoginProps & WithI18nProps, LoginState> {
  state: LoginState = {
    subscribe: false,
  };

  handleShimClick = () => {
    const {onClose = NOOP} = this.props;
    onClose();
  };

  handleSubscribeChange = () => {
    this.setState((prevState) => ({
      subscribe: !prevState.subscribe,
    }));
  };

  handleLoginClick = (method) => {
    const {path} = method;
    const {subscribe} = this.state;
    window.location.assign(
      `${path}?subscribe=${subscribe}&return_to=${encodeURIComponent(window.location.pathname)}`,
    );
  };

  handleKeyDown = (event) => {
    const {show, onClose = NOOP} = this.props;
    if (show && event.key === 'Escape') {
      onClose();
    }
  };

  componentDidMount() {
    window.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
  }

  render() {
    const {i18n, methods, show} = this.props;

    return (
      <aside
        className={classNames(style.Shim, {[style.Open]: show})}
        onClick={this.handleShimClick}
      >
        <FocusTrap active={show}>
          {(focusRef) => (
            <section ref={focusRef} className={style.Modal} onClick={preventEventBubbling}>
              <Flex direction="column" wrapperComponent="header" className={style.Header}>
                <button className={style.CloseButton} type="button" onClick={this.handleShimClick}>
                  <Icon size={16} icon="close" />
                </button>
                <Text
                  type="h2"
                  inheritColor
                  text={i18n.translate('header')}
                  className={style.HeaderText}
                />
                <Text inheritColor text={i18n.translate('subhead')} className={style.HeaderText} />
              </Flex>
              <Flex direction="column" className={style.Content}>
                {methods.map((method) => (
                  <LoginButton key={method.name} method={method} onClick={this.handleLoginClick} />
                ))}
                <CheckBox
                  name="subscribe"
                  label={<Text type="small" text={i18n.translate('subscribeText')} />}
                  onChange={this.handleSubscribeChange}
                />
              </Flex>
              <footer className={style.Footer}>
                <Text type="small" component="p" resetMargin>
                  {i18n.translate('legalCopy', {
                    link: (
                      <Link
                        rel="noopener noreferrer"
                        type="small"
                        forcePink
                        url={i18n.translate('termsLink')}
                        text={i18n.translate('termsText')}
                        newTab
                      />
                    ),
                  })}
                </Text>
              </footer>
            </section>
          )}
        </FocusTrap>
      </aside>
    );
  }
}

export const Login = withI18n({
  id: 'Login',
  fallback: locales.en,
})(LoginComponent);
